"use strict";

/**
 * Namespace für Layer.
 *
 * @namespace
 * @memberof gc.gis
 */
gc.gis.layer = gc.gis.layer || {};

/**
 * Layer-Typen
 *
 * @enum {string}
 * @memberof gc.gis.layer
 */
gc.gis.layer.GCLayerType = {
	/**
	 * lokaler Gruppenlayer/Kategorie
	 */
	GROUP:        'group',
	/**
	 * interner Fachdatenbank-Layer (Raster)
	 */
	FACHDB:       'fachdb',
	/**
	 * interner PostGIS-Layer (Raster)
	 */
	POSTGIS:      'postgis',
	/**
	 * interner Datei-Layer (Raster)
	 */
	FILE:         'file',
	/**
	 * interner ATKIS-Layer (Raster)
	 */
	ATKIS:        'atkis',	// FIXME: CSC fragen: GCDtmMapLayer::CON_TYPE_ATKIS
	/**
	 * interner WMS-Layer (Raster)
	 */
	INTERNAL_WMS: 'wms',
	/**
	 * interner WFS-Layer (Vektor)
	 */
	INTERNAL_WFS: 'wfs',
	/**
	 * externer WMS-Layer (Raster)
	 */
	EXTERNAL_WMS: 'external_wms',
	/**
	 * externer WFS-Layer (Vektor)
	 */
	EXTERNAL_WFS: 'external_wfs'
};

/**
 * Callback zum bestimmen, ob ein Layer einem Kriterium entspricht.
 *
 * @callback gc.gis.layer.GCLayerFilterCallback
 * @param {gc.gis.layer.GCAbstractLayer} layer Layerinstanz
 * @return {boolean} Treffer?
 */
gc.gis.layer.GCLayerFilterCallback;

/**
 * Layer-Event-Typen
 *
 * @enum {string}
 */
gc.gis.layer.GCLayerEvent = {
	/**
	 * Bei auf-/zuklappen des Layers.
	 *
	 * @event gc.gis.layer.GCLayerEvent#change:expand
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer
	 * @property {boolean} oldValue
	 * @property {boolean} newValue
	 */
	EXPANDED_CHANGED:   'change:expand',
	/**
	 * Bei Änderung der Sichtbarkeit des Layers.
	 *
	 * @event gc.gis.layer.GCLayerEvent#change:visibility
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer
	 * @property {boolean} oldValue
	 * @property {boolean} newValue
	 */
	VISIBILITY_CHANGED: 'change:visibility',
	/**
	 * Bei Aus-/Abwahl des Layers.
	 *
	 * @event gc.gis.layer.GCLayerEvent#change:selection
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer
	 * @property {boolean} oldValue
	 * @property {boolean} newValue
	 */
	SELECTION_CHANGED:  'change:selection',
	/**
	 * Bei Umbenennen des Layers.
	 *
	 * @event gc.gis.layer.GCLayerEvent#change:label
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer
	 * @property {string} oldValue
	 * @property {string} newValue
	 */
	LABEL_CHANGED:      'change:label',
	/**
	 * Bei Änderung der Deckkraft.
	 *
	 * @event gc.gis.layer.GCLayerEvent#change:opacity
	 * @type {Object}
	 * @property {gc.gis.layer.GCAbstractLayer} layer
	 * @property {number} oldValue
	 * @property {number} newValue
	 */
	OPACITY_CHANGED:    'change:opacity'
};

/**
 * Vector-Layer-Events
 *
 * @enum {string}
 */
gc.gis.layer.GCVectorLayerEvent = {
	/**
	 * Wird ausgelöst, wenn ein Feature dem Layer hinzugefügt wird.
	 *
	 * @event gc.gis.layer.GCVectorLayerEvent#feature:add
	 */
	FEATURE_ADDED:					'feature:add',
	/**
	 * Wird ausgelöst, wenn ein Feature vom Layer entfernt wird.
	 *
	 * @event gc.gis.layer.GCVectorLayerEvent#feature:remove
	 */
	FEATURE_REMOVED:                'feature:remove',
	/**
	 * Wird ausgelöst, wenn ein Feature des Layers geändert wird wird.
	 *
	 * @event gc.gis.layer.GCVectorLayerEvent#feature:change
	 */
	FEATURE_CHANGED:                'feature:change',
	/**
	 * Wird ausgelöst, wenn alle Features des Layers zurückgesetzt werden.
	 *
	 * @event gc.gis.layer.GCVectorLayerEvent#layer:clear
	 */
	LAYER_CLEARED:                  'layer:clear'
};

/**
 * Raster-Layer-Events
 *
 * @enum {string}
 */
gc.gis.layer.GCRasterLayerEvent = {

};

/**
 * @classdesc
 * Abstrakte Basisklasse für Layer.
 *
 * @abstract
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis.layer
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {gc.gis.util.GCTreeNode} node Knoteninstanz im Layerbaum
 * @param {Object<string,*>} options
 *      Aufrufoptionen. Es gibt zwei Arten von Layereigenschaften:
 *      1. statische, 2. dynamische. Statische Eigenschaften sind nicht dazu
 *      vorgesehen sich zu ändern (daher auch keine <code>public</code>
 *      setter-Methoden) und werden i.d.R. serverseitig mitgeliefert.
 *      Dynamische Eigenschaften stellen <em>aktuelle</em> Einstellungen dar.
 * @param {string} options.type
 *      (statische Eigenschaft) {@link gc.gis.layer.GCLayerType|Layertyp}
 * @param {string} options.originalType
 *      (statische Eigenschaft) Ursprünglicher (serverseitiger) {@link gc.gis.layer.GCLayerType|Layertyp}
 * @param {Object[]} [options.actions=[]]
 *      (statische Eigenschaft) Diverse Aktionen, die über den Layer gestartet werden können
 * @param {boolean} [options.expanded=false]
 *      (dynamische Eigenschaft) Ausgeklappt?
 * @param {boolean} [options.selected=false]
 *      (dynamische Eigenschaft) Selektiert?
 * @param {boolean} [options.visible=false]
 *      (dynamische Eigenschaft) Sichtbar?
 * @param {string} [options.label=null]
 *      (dynamische Eigenschaft) <em>Aktuelle</em> Bezeichnung (siehe auch <code>options.defaultLabel</code>)
 * @param {string} [options.defaultLabel='Unbenannt']
 *      (statische Eigenschaft) Originale (serverseitige) Bezeichnung (siehe auch <code>options.label</code>)
 * @param {number} [options.opacity=1.0]
 *      (dynamische Eigenschaft) Deckkraft <code>[0,1]</code>
 */
gc.gis.layer.GCAbstractLayer = function( node, options )
{
	gc.gis.GCObservable.call( this );

	gc.gis.assert.isSet( node );
	gc.gis.assert.isSet( options );

	/**
	 * @protected
	 * @type {gc.gis.util.GCTreeNode}
	 */
	this._node   = node;
	this._initEvents();

	var defaultLabel = options.defaultLabel || 'Unbenannt';
	var label = options.label || null;

	/**
	 * Enthält statische Eigenschaften.
	 *
	 * @protected
	 * @type {Object<string,*>}
	 */
	this._config = {
		defaultLabel:   defaultLabel,
		type:           options.type,
		originalType:   options.originalType,
		abstract:		options.abstract || '',
		actions:        options.actions || []
	};

	/**
	 * Enthält dynamische Eigenschaften.
	 *
	 * @protected
	 * @type {Object<string,*>}
	 */
	this._states = {
		expanded: gc.gis.GCUtil.parseBool( options.expanded, false ),
		selected: gc.gis.GCUtil.parseBool( options.selected, false ),
		visible:  gc.gis.GCUtil.parseBool( options.visible, false ),
		label:    label,
		opacity:  parseFloat( options.opacity ) || 1.0
	};
};
gc.gis.layer.GCAbstractLayer.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Initialisiert Layertree-Events.
 *
 * @protected
 * @method
 * @see {gc.gis.util.GCTreeNode}
 */
gc.gis.layer.GCAbstractLayer.prototype._initEvents = function()
{
	var self = this;

    // GCTreeNodeEvent, die von den Layern-Nodes ausgelöst werden abfangen und in gültige GCMapEvents überführen.
	// Das Durchreichen der Events im Baum nach oben erfolgt durch die Klasse GCTreeNode siehe Methode _initEvents.
	this._node.on( gc.gis.util.GCTreeNodeEvent.CHILDREN_CHANGED, this, function(evt ) {
		var layers = [];
		for( var i=0; i<evt.children.length; i++ )
		{
			layers.push( evt.children[i].getPayload() );
		}
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_ADDED, {
			layers: layers
		});
	});
	this._node.on( gc.gis.util.GCTreeNodeEvent.CHILDREN_ADDED, this, function(evt ) {
		var layers = [];
		for( var i=0; i<evt.children.length; i++ )
		{
			layers.push( evt.children[i].getPayload() );
		}
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_ADDED, {
			layers: layers
		});
	});
	this._node.on(gc.gis.util.GCTreeNodeEvent.CHILDREN_REMOVED, this, function(evt) {
		var layers = [], i, ii;
		for(i = 0, ii = evt.children.length; i < ii; i++)
		{
			layers.push(evt.children[i].getPayload());
		}
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_REMOVED, {
			layers: layers
		});
	});
	this._node.on( gc.gis.util.GCTreeNodeEvent.CHILD_ADDED, this, function(evt ) {
		self._fireEvent( gc.gis.map.GCMapEvent.LAYER_ADDED, {
			layers: [evt.child.getPayload()]
		});
	});
	this._node.on(gc.gis.util.GCTreeNodeEvent.CHILD_REMOVED, this, function(evt) {
		var layers = [], i, ii;
		for(i = 0, ii = evt.children.length; i < ii; i++)
		{
			layers.push(evt.children[i].getPayload());
		}
		self._fireEvent(gc.gis.map.GCMapEvent.LAYER_REMOVED, {
			layers: layers
		});
	});

	// GCMapEvent, die direkt von den Layern ausgelöst werden nach oben durchreichen. Die Events werden vom Root-Layer
	// im Mapview gefangen und von dort an die Module verteilt.
	// !!! keine Events hier Durchreichen (bubblen), die bereits von GCTreeNode gebubbeld werden, da diese sonst doppelt
	// ausgelöst werden. Ggf. muss das Durchreichen komplett hier erfolgen.
    var bubble = function(evt_type, evt)
	{
        if(self.getParent())
        {
            self.getParent()._fireEvent(evt_type, evt);
        }
    };

	this.on(gc.gis.map.GCMapEvent.LAYER_MOVED, this, function(evt) {
		bubble(gc.gis.map.GCMapEvent.LAYER_MOVED, evt);
	});
};

/**
 * Sammelt die statischen und dynamischen Layereigenschaften und gibt sie in
 * einem Objekt zurück. Das hier zurückgegebene Objekt kann direkt dazu
 * verwendet werden, eine neue Layerinstanz zu erstellen.
 *
 * @public
 * @method
 * @return {Object} Objektrepräsentation des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.toObject = function()
{
	const obj = {};
	const config = this.getConfig();
	const states = this.getStates();
	const keys_config = Object.keys( config );

	for (let i = 0; i < keys_config.length; i++)
	{
		obj[keys_config[i]] = config[keys_config[i]];
	}
	const keys_states = Object.keys(states);
	for (let i = 0; i < keys_states.length; i++)
	{
		obj[keys_states[i]] = states[keys_states[i]];
	}

	return obj;
};

/**
 * @protected
 * @method
 * @return {gc.gis.util.GCTreeNode} Knoteninstanz im Layerbaum
 */
gc.gis.layer.GCAbstractLayer.prototype._getNode = function()
{
	return this._node;
};

/**
 * Gibt den Indexpfad des Layers im Layerbaum zurück
 *
 * @public
 * @method
 * @return {number[]} Indexpfad im Layerbaum
 */
gc.gis.layer.GCAbstractLayer.prototype.getIndex = function()
{
	return this._getNode().getPath();
};

/**
 * Gibt den Eltern-Gruppenlayer dieses Layers zurück, sofern vorhanden.
 *
 * @public
 * @method
 * @return {gc.gis.layer.GCGroupLayer|null}
 */
gc.gis.layer.GCAbstractLayer.prototype.getParent = function()
{
	const p = this._getNode().getParent();
	return p ? p.getPayload() : null;
};

/**
 * Gibt eine bzw. alle statische Eigenschaft(en) zurück.
 *
 * @public
 * @method
 * @param {string} [opt_key]  Optionaler Eigenschaftsschlüssel
 * @return {*} einzelner Eigenschaftswert, wenn Schlüssel angegeben oder Objekt mit allen Eigenschaften
 */
gc.gis.layer.GCAbstractLayer.prototype.getConfig = function(opt_key)
{
	if (typeof opt_key === 'string')
	{
		return this._config[opt_key];
	}
	return this._config;
};

/**
 * Setzt eine statische Eigenschaft. Sollte nur im Konstruktor verwendet werden.
 *
 * @protected
 * @method
 * @param {!string} key Eigenschaftsschlüssel
 * @param {*} value  Eigenschaftswert
 */
gc.gis.layer.GCAbstractLayer.prototype._setConfig = function(key, value)
{
	this._config[key] = value;
};

/**
 * @public
 * @method
 * @return {Object<string,any>} Objekt mit allen dynamischen Eigenschaften
 */
gc.gis.layer.GCAbstractLayer.prototype.getStates = function()
{
	return this._states;
};

/**
 * Gibt eine dynamische Eigenschaft zurück.
 *
 * @protected
 * @method
 * @param {!string} key  Eigenschaftsschlüssel
 * @return {*} dynamischer Eigenschaftswert
 */
gc.gis.layer.GCAbstractLayer.prototype._getState = function( key )
{
	return this._states[key];
};

/**
 * Setzt eine dynamische Eigenschaft.
 *
 * @protected
 * @method
 * @param {!string} key Eigenschaftsschlüssel
 * @param {*} value  Eigenschaftswert
 */
gc.gis.layer.GCAbstractLayer.prototype._setState = function( key, value )
{
	this._states[key] = value;
};

/**
 * @public
 * @abstract
 * @method
 * @param {function} callback Callback, in der die Ausdehnung verarbeitet wird.
 *   1 Argument: {layer: Object, extent: [minx, miny, maxx, maxy]}
 * @param {string} [opt_crs] Optionales Zielkoordinatenreferenzsystem;
 *   Default ist {@link gc.gis.map.GCMapView#getProjection}
 */
gc.gis.layer.GCAbstractLayer.prototype.getExtent = gc.gis.abstractMethod;

/**
 * @public
 * @abstract
 * @method
 * @param {string} [opt_crs] Optionales Zielkoordinatenreferenzsystem;
 *   Default ist {@link gc.gis.map.GCMapView#getProjection}
 */
gc.gis.layer.GCAbstractLayer.prototype.getExtentSync = gc.gis.abstractMethod;

/**
 * Zeigt an, ob der Layer abfragbar ist.
 * @public
 * @abstract
 * @method
 * @return {boolean}
 */
gc.gis.layer.GCAbstractLayer.prototype.isQueryable = gc.gis.abstractMethod;

/**
 * @public
 * @method
 * @return {boolean} Ist der Layer ausgeklappt?
 */
gc.gis.layer.GCAbstractLayer.prototype.isExpanded = function()
{
	return this._getState('expanded');
};

/**
 * @public
 * @method
 * @param {boolean} state  Ausklappstatus des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.setExpanded = function( state )
{
	const old = this.isExpanded();

	state = state === true;
	this._setState('expanded', state );
	this._fireEvent(gc.gis.layer.GCLayerEvent.EXPANDED_CHANGED, {
		layer: this,
		oldValue: old,
		newValue: state
	});
};

/**
 * @public
 * @method
 * @return {boolean} Ist der Layer selektiert?
 */
gc.gis.layer.GCAbstractLayer.prototype.isSelected = function()
{
	return this._getState('selected');
};

/**
 * Prüft, ob einer der Elternlayer dieses Layers, selektiert ist.
 *
 * @public
 * @method
 * @return {boolean} Ist einer der Elternlayer selektiert?
 */
gc.gis.layer.GCAbstractLayer.prototype.isParentSelected = function()
{
	return (this.getParent() ? this.getParent().isParentSelected() : false);
};

/**
 * @public
 * @method
 * @param {boolean} state  Layer selektieren?
 */
gc.gis.layer.GCAbstractLayer.prototype.setSelected = function(state)
{
	const old = this.isSelected();

	state = state === true;
	this._setState('selected', state );
	this._fireEvent(gc.gis.layer.GCLayerEvent.SELECTION_CHANGED, {
		layer: this,
		oldValue: old,
		newValue: state
	});
};

/**
 * @public
 * @method
 * @return {boolean} Ist der Layer sichtbar?
 */
gc.gis.layer.GCAbstractLayer.prototype.isVisible = function()
{
	return this._getState('visible');
};

/**
 * @public
 * @method
 * @param {boolean} state  Sichtbarkeitsstatus des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.setVisible = function( state )
{
	const old = this.isVisible();

	state = state === true;
	this._setState('visible', state );
	this._fireEvent(gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, {
		layer: this,
		oldValue: old,
		newValue: state
	});
};

/**
 * Prüft anhand der Sichtbarkeit der Elternlayer dieses Layers, ob dieser Layer
 * tatsächlich sichtbar ist.
 *
 * @public
 * @method
 * @return {boolean} Ist der Layer tatsächlich sichtbar?
 */
gc.gis.layer.GCAbstractLayer.prototype.isReallyVisible = function()
{
	return this.isVisible()
		&& (this.getParent() ? this.getParent().isReallyVisible() : true);
};

/**
 * @public
 * @method
 * @return {number} Deckkraft des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.getOpacity = function()
{
	return this._getState('opacity');
};

/**
 * @public
 * @method
 * @param {number} opacity  Deckkraft des Layers [0:1]
 */
gc.gis.layer.GCAbstractLayer.prototype.setOpacity = function( opacity )
{
	const old = this.getOpacity();

	this._setState('opacity', opacity);
	this._fireEvent(gc.gis.layer.GCLayerEvent.OPACITY_CHANGED, {
		layer: this,
		oldValue: old,
		newValue: opacity
	});
};

/**
 * @public
 * @method
 * @return {string} aktuelle Bezeichnung des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.getLabel = function()
{
	return this._getState('label') != null ? this._getState('label') : this.getDefaultLabel();
};

/**
 * @public
 * @method
 * @param {string} label  neue Bezeichnung des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.setLabel = function(label)
{
	const old = this.getLabel();

	this._setState('label', label);
	this._fireEvent(gc.gis.layer.GCLayerEvent.LABEL_CHANGED, {
		layer: this,
		oldValue: old,
		newValue: label
	});
};

/**
 * @public
 * @method
 * @return {string} Originalbezeichnung des Layers
 */
gc.gis.layer.GCAbstractLayer.prototype.getDefaultLabel = function()
{
	return this.getConfig('defaultLabel');
};

/**
 * @public
 * @method
 * @returns {String}
 */
gc.gis.layer.GCAbstractLayer.prototype.getAbstract = function()
{
	return this.getConfig('abstract');
};

/**
 * @public
 * @method
 * @returns {gc.gis.layer.GCLayerType}
 */
gc.gis.layer.GCAbstractLayer.prototype.getType = function()
{
	return this.getConfig('originalType');
};

/**
 * @public
 * @method
 * @returns {Object[]}
 */
gc.gis.layer.GCAbstractLayer.prototype.getActions = function()
{
	return this.getConfig('actions');
};

/**
 * Setter-Methode, um den Zugriff auf die Layerinstanz aus der verwendeten
 * Kartenbibliothek, die diesen Layer repräsentiert, zu ermöglichen.
 *
 * @public
 * @method
 * @param {*} impl Layerinstanz der Kartenbibliothek
 * @see {gc.gis.map.GCLayerTreeManager}
 */
gc.gis.layer.GCAbstractLayer.prototype.setImpl = function( impl )
{
	this._impl = impl;
};

/**
 * Getter-Methode, um den Zugriff auf die Layerinstanz aus der verwendeten
 * Kartenbibliothek, die diesen Layer repräsentiert, zu ermöglichen.
 *
 * @public
 * @method
 * @return {*} Layerinstanz der Kartenbibliothek
 * @see {gc.gis.map.GCLayerTreeManager}
 */
gc.gis.layer.GCAbstractLayer.prototype.getImpl = function()
{
	return this._impl;
};
